import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, AlertCircle, EyeOff,
} from "lucide-react";
import { Card, CardContent } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Badge } from "@cms/ui/components/badge";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@cms/ui/components/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@cms/ui/components/dialog";
import { ConfirmDeleteDialog } from "@cms/ui/components/confirm-dialog";
import { LoadingSpinner } from "@cms/ui/components/loading-spinner";
import { MenuModuleGuard, MenuModuleHeader } from "../components/menu-module-shell";
import { MenuPageSettingsCard } from "../components/menu-page-settings";
import { moveItem } from "../lib/menus";
import {
  fetchMenuModel, createMenu, updateMenu, deleteMenu, reorderMenus, type Menu,
} from "../lib/menu-module";
import type { ClientOutletContext } from "../lib/outlet-context";

function MenusList({ api, refreshStatus }: Pick<ClientOutletContext, "api" | "refreshStatus">) {
  const navigate = useNavigate();
  const [menus, setMenus] = useState<Menu[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [createError, setCreateError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Menu | null>(null);

  const load = useCallback(() => {
    if (!api) return;
    fetchMenuModel(api)
      .then((data) => setMenus(data.menus))
      .catch((err: Error) => setError(err.message));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!api) return;
    const name = form.name.trim();
    if (!name) {
      setCreateError("Enter a menu name.");
      return;
    }
    setBusy(true);
    setCreateError("");
    try {
      const result = await createMenu(api, {
        name,
        description: form.description.trim() || null,
      });
      setCreateOpen(false);
      refreshStatus?.();
      navigate(`/menu/${result.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, delta: -1 | 1) => {
    if (!api || !menus) return;
    const next = moveItem(menus, index, delta);
    if (next === menus) return;
    const previous = menus;
    setMenus(next);
    try {
      await reorderMenus(api, next.map((menu) => menu.id));
      refreshStatus?.();
    } catch (err) {
      setMenus(previous);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleActive = async (menu: Menu) => {
    if (!api) return;
    setBusy(true);
    setError("");
    try {
      await updateMenu(api, menu.id, { active: !menu.active });
      load();
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!api || !deleteTarget) return;
    setBusy(true);
    setError("");
    try {
      await deleteMenu(api, deleteTarget.id);
      setDeleteTarget(null);
      load();
      refreshStatus?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <MenuModuleHeader
        action={
          <Button size="sm" onClick={() => { setForm({ name: "", description: "" }); setCreateError(""); setCreateOpen(true); }}>
            <Plus className="h-4 w-4" />
            Create menu
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!menus && !error && <LoadingSpinner className="py-20" />}

      {menus && menus.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No menus yet — create your first menu (e.g. Lunch, Dinner or Drinks).
          </CardContent>
        </Card>
      )}

      {menus && menus.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Menu</th>
                  <th className="px-4 py-3 font-medium">Sections</th>
                  <th className="px-4 py-3 font-medium">Items</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {menus.map((menu, index) => {
                  const itemCount = menu.sections.reduce((sum, section) => sum + section.items.length, 0);
                  return (
                    <tr key={menu.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-2 font-medium">
                          <Link to={`/menu/${menu.id}`} className="hover:underline">{menu.name}</Link>
                          {!menu.active && (
                            <Badge variant="secondary" className="gap-1">
                              <EyeOff className="h-3 w-3" />
                              Hidden
                            </Badge>
                          )}
                        </span>
                        {menu.description && (
                          <span className="block text-xs text-muted-foreground">{menu.description}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{menu.sections.length}</td>
                      <td className="px-4 py-3 text-muted-foreground">{itemCount}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm" aria-label="Move up"
                            disabled={index === 0 || busy}
                            onClick={() => move(index, -1)}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="sm" aria-label="Move down"
                            disabled={index === menus.length - 1 || busy}
                            onClick={() => move(index, 1)}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" disabled={busy} onClick={() => toggleActive(menu)}>
                            {menu.active ? "Hide" : "Show"}
                          </Button>
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/menu/${menu.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                              Edit
                            </Link>
                          </Button>
                          <Button
                            variant="ghost" size="sm" aria-label="Delete menu"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteTarget(menu)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {api && <MenuPageSettingsCard api={api} refreshStatus={refreshStatus} />}

      {/* Create menu */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a menu</DialogTitle>
            <DialogDescription>
              A menu is a full card your customers see — like Lunch, Dinner or Drinks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-menu-name">Name</Label>
              <Input
                id="new-menu-name"
                value={form.name}
                maxLength={100}
                autoFocus
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && create()}
                placeholder="e.g. Dinner"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-menu-description">Description (optional)</Label>
              <Input
                id="new-menu-description"
                value={form.description}
                maxLength={500}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. Served from 5pm"
              />
            </div>
            {createError && <p className="text-sm text-destructive">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={create} disabled={busy || !form.name.trim()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create menu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.name}"?`}
        description="This deletes the menu and its sections. The dishes themselves stay in your Items list (they may be on other menus). This can't be undone — use Hide if it's only off for now."
        confirmLabel="Delete menu"
        busy={busy}
        onConfirm={remove}
      />
    </div>
  );
}

export function MenuMenusPage() {
  return (
    <MenuModuleGuard title="Menu" description="Manage your restaurant menus.">
      {(ctx) => <MenusList api={ctx.api} refreshStatus={ctx.refreshStatus} />}
    </MenuModuleGuard>
  );
}
